import { useQueryClient } from '@tanstack/react-query';
import { Form } from 'connex-cds';
import React from 'react';
import { useParams } from 'react-router-dom';

import { useTicketPdfContext } from '../../../../../query-hooks/ticket-pdf-context';
import { Logger } from '../../../../../util/log/logger';
import { DEFAULT_COC_SETUP_TYPE } from '../cocSetupDefaults';
import { useCompanySetup, useCreateSummary, useGetSummary, useUpdateSummary } from '../MasterDataProvider';
import { useTicketContext } from '../TicketContext';
import { useResolvedTicketTimeZone } from '../useResolvedTicketTimeZone';
import { cocSetupQueryOptions, useGetCocSetup } from '../wizard/steps/dot-workflow/DotWorkflow';
import { computeConstituentsFromConsumptions } from './buildTemplateContext';
import { useTemplate } from './qr-code/useTemplate';
import { useIsLinkBasedQr } from './useIsLinkBasedQr';
import { buildBatchData, buildQrSyncSignature, buildWaterQrContext, usePreparedContext } from './usePreparedContext';

const QrSummaryContext = React.createContext();

const idleQrSummary = {
  summaryRef: undefined,
  loading: false,
  isReadyToSync: true,
  syncQrSummary: async () => {},
};

/**
 * A link-based QR resolves to the stored `summary` item rather than to live ticket data, so whatever
 * is in that item when someone scans is what they see. Batch target/actual quantities can sync
 * minutes after the driver reaches the job site, which is why this lives above the wizard instead of
 * inside the QR tab: the tab is only mounted while the drawer is open, so a printed link used to
 * keep resolving to its pre-batch snapshot until somebody happened to open that tab.
 */
export const useQrSummary = () => React.useContext(QrSummaryContext) ?? {};

export const QrSummaryProvider = ({ children }) => {
  const companySetup = useCompanySetup();
  const { ticket } = useTicketContext();
  const isLinkBased = useIsLinkBasedQr();
  const enabled = companySetup?.data?.isQrCodeEnabled && ticket?.customData?.ticketType !== 'PMP';

  if (!enabled) {
    return <QrSummaryContext.Provider value={idleQrSummary}>{children}</QrSummaryContext.Provider>;
  }

  return (
    <QrSummaryRuntime isLinkBased={isLinkBased} ticket={ticket}>
      {children}
    </QrSummaryRuntime>
  );
};

const QrSummaryRuntime = ({ children, isLinkBased, ticket }) => {
  const { entityRef } = useParams();
  const queryClient = useQueryClient();
  const companySetup = useCompanySetup();
  const timeZone = useResolvedTicketTimeZone();
  const { values } = Form.useFormContext() ?? {};
  const { isValid, compile } = useTemplate();
  const cocSetup = useGetCocSetup();

  const ticketRef = ticket?.crn;
  const cocSetupType = companySetup?.data?.batchMaterialSummaryTable ?? DEFAULT_COC_SETUP_TYPE;

  const { data, loading } = useGetSummary(isLinkBased ? ticketRef : undefined);
  const { context: pdfContext, isFetched: isPdfContextFetched } = useTicketPdfContext({ ticketRef });
  const create = useCreateSummary();
  const update = useUpdateSummary();

  const cocData = React.useMemo(() => {
    if (!cocSetup?.isLoading) {
      return cocSetup?.data?.testsResult?.consumptions || [];
    }

    return null;
  }, [cocSetup?.isLoading, cocSetup?.data]);

  const batch = cocSetup?.data?.testsResult?.batch;

  const preparedContext = usePreparedContext({
    ticket,
    mobileTicket: values,
    cocData,
    pdfContext,
    timeZone,
    batch,
  });

  const stringData = React.useMemo(() => {
    if (isValid) {
      return compile(preparedContext);
    }

    return null;
  }, [compile, isValid, preparedContext]);

  const [summaryRef, setSummaryRef] = React.useState();

  const lastSyncedContext = React.useRef();
  const storedSummary = React.useRef();
  const latestPreparedContext = React.useRef();

  React.useEffect(() => {
    latestPreparedContext.current = preparedContext;
  }, [preparedContext]);

  React.useEffect(() => {
    if (!data?.crn) {
      return;
    }

    const current = storedSummary.current;
    if (!current?.crn) {
      storedSummary.current = data;
      return;
    }

    // Keep the GET identity so a CREATE from another writer cannot drop the stored summary item.
    if (current?.crn === data?.crn) {
      storedSummary.current = { ...data, context: current?.context ?? data?.context };
    }
  }, [data]);

  const saveSummary = React.useCallback(
    async context => {
      const existing = storedSummary.current?.crn ? storedSummary.current : undefined;
      const item = existing ? { ...existing, context } : { crn: ticketRef, context };

      try {
        const summary = await (existing ? update : create)(item);

        storedSummary.current = summary?.crn ? summary : item;
        lastSyncedContext.current = context;
        setSummaryRef(ticketRef);

        return true;
      } catch (error) {
        Logger.error(`Mobile Ticket - QrSummaryProvider - saveSummary: ${error?.message}`);
        return false;
      }
    },
    [create, ticketRef, update]
  );

  const writeQueue = React.useRef(Promise.resolve(false));

  const enqueueSave = React.useCallback(
    context => {
      const run = () => {
        if (!context) {
          return false;
        }

        if (lastSyncedContext.current === context || storedSummary.current?.context === context) {
          lastSyncedContext.current = context;
          return true;
        }

        return saveSummary(context);
      };

      const next = writeQueue.current.then(run, run);

      writeQueue.current = next.catch(() => false);

      return next;
    },
    [saveSummary]
  );

  const enqueueSaveRef = React.useRef(enqueueSave);

  React.useEffect(() => {
    enqueueSaveRef.current = enqueueSave;
  }, [enqueueSave]);

  const batchSignature = React.useMemo(() => {
    if (cocData === null) {
      return null;
    }

    return buildQrSyncSignature(cocData, batch);
  }, [batch, cocData]);

  const lastSyncedBatch = React.useRef();

  React.useEffect(() => {
    if (isLinkBased && data?.crn) {
      setSummaryRef(data?.crn);
    }
  }, [data, isLinkBased]);

  React.useEffect(() => {
    if (!isLinkBased) {
      setSummaryRef(stringData);
      return;
    }

    // pdfContext fills customerJob / watermarks / origin phone after the first compile. Saving
    // before that, then keying the skip on batchSignature alone, would freeze the blank snapshot
    // until batch or water data actually changes — including for scans that never open the QR tab.
    if (loading !== false || !isPdfContextFetched || !stringData || batchSignature === null) {
      return;
    }

    if (lastSyncedBatch.current === batchSignature) {
      return;
    }

    // Single load-time writer: wait until GET, PDF, and CoC are ready. Do not also fire
    // syncQrSummary here — that path is for later refreshes (QR tab, submit, finalize).
    enqueueSaveRef.current(stringData).then(saved => {
      if (saved) {
        lastSyncedBatch.current = batchSignature;
      }
    });
  }, [batchSignature, isLinkBased, isPdfContextFetched, loading, stringData]);

  const syncQrSummary = React.useCallback(async () => {
    if (!isLinkBased || !ticketRef || !isValid || !isPdfContextFetched || !latestPreparedContext.current) {
      return;
    }

    try {
      const cocOptions = cocSetupQueryOptions({ entityRef, ticketRef, cocSetupType });
      let fresh;

      try {
        fresh = await queryClient.fetchQuery({
          ...cocOptions,
          staleTime: 0,
        });
      } catch (fetchError) {
        fresh = queryClient.getQueryData(cocOptions?.queryKey);
        if (!fresh) {
          throw fetchError;
        }
        Logger.error(
          `Mobile Ticket - QrSummaryProvider - syncQrSummary using cached coc-setup: ${fetchError?.message}`
        );
      }

      const consumptions = fresh?.testsResult?.consumptions ?? [];
      const { concreteConstituents, admixConstituents } = computeConstituentsFromConsumptions(consumptions);
      const batchData = buildBatchData(consumptions);
      const waterContext = buildWaterQrContext(fresh?.testsResult?.batch);

      const compiled = compile({
        ...latestPreparedContext.current,
        ...batchData,
        ...waterContext,
        concreteConstituents,
        admixConstituents,
      });

      const saved = await enqueueSave(compiled);

      if (saved) {
        lastSyncedBatch.current = buildQrSyncSignature(consumptions, fresh?.testsResult?.batch);
      }
    } catch (error) {
      Logger.error(`Mobile Ticket - QrSummaryProvider - syncQrSummary: ${error?.message}`);
    }
  }, [
    compile,
    cocSetupType,
    enqueueSave,
    entityRef,
    isLinkBased,
    isPdfContextFetched,
    isValid,
    queryClient,
    ticketRef,
  ]);

  const value = React.useMemo(
    () => ({
      summaryRef,
      loading: isLinkBased ? loading : false,
      isReadyToSync: isPdfContextFetched,
      syncQrSummary,
    }),
    [isLinkBased, isPdfContextFetched, loading, summaryRef, syncQrSummary]
  );

  return <QrSummaryContext.Provider value={value}>{children}</QrSummaryContext.Provider>;
};
